import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { IProjectAOI } from "../../interfaces/i-project-aoi";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../shared/model/google-analytics-constants";
import { MapService } from "../../services/internal-services/map.service";
import { FeatureFlagsService } from "../../services/internal-services/feature-flags.service";
import { FeatureFlags } from "../../model/feature-flags";
import { GoogleAnalyticsService } from "../../../shared/services/google-analytics.service";
import { VIEWER_SERVICE } from "../../services/internal-services/viewer-services/inject-base-viewer.service";
import { LoadingService } from "../../services/internal-services/loading.service";
import { Subscription } from "rxjs";
import { OverlayModule, ConnectedPosition } from "@angular/cdk/overlay";
import { CdkDrag, CdkDragHandle, CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { Viewerlayer } from "../../model/viewerlayer";
import { LayerOverlayPanelComponent } from "../layer-managers/layer-overlay-panel/layer-overlay-panel.component";
import { ChatContainerComponent, ChatPanelHdrComponent } from "../../../ai-components/chat";
import type { AgentDefinition } from "../../../ai-components/chat/model/agent.types";
import { AiChatSessionService } from "../../../ai-components/chat/services/ai-chat-session.service";
import { ChatOverlayResizeDirective } from "../../../shared-components/directives/chat-overlay-resize.directive";

/**
 * Compact Map Controller Component
 * Vertical compact control panel for map interactions including zoom, mode switching (2D/3D),
 * integrated compass navigation, infrared selector, and layer management.
 * Standalone component using Angular 16+ syntax (@if, @for).
 * Reuses logic from map-controller for consistency.
 * Compass integrated inline with hover-expandable orientation ring.
 */
@Component({
  selector: "app-compact-map-controller",
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    OverlayModule,
    LayerOverlayPanelComponent,
    ChatContainerComponent,
    ChatPanelHdrComponent,
    CdkDrag,
    CdkDragHandle,
    ChatOverlayResizeDirective,
  ],
  templateUrl: "./compact-map-controller.component.html",
  styleUrl: "./compact-map-controller.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompactMapControllerComponent implements OnInit, AfterViewInit, OnDestroy {
  public viewerService = inject(VIEWER_SERVICE);

  FeatureFlags = FeatureFlags;

  private _orientation: string;

  private _mode: any;

  // Compass hover state
  compassHovered = false;

  enableObliqueMode: boolean;

  enableObliqueByZoom: boolean;

  private _centerAOI: IProjectAOI;

  private _aois: IProjectAOI[];

  private _imageAOIs: IProjectAOI[];

  controllerRotation = 0;

  textRotation = 0;

  lockRotation = false;

  showInfraredButton3D = false;

  enableInfraredButton = false;

  is3D: boolean = false;

  lockZoomIn: boolean = false;

  lockZoomOut: boolean = false;

  // Loading state for spinner
  showLoading = false;

  // Layer panel state
  layerPanelOpen = false;

  isImageryShown = true;

  autoContrastAllowed = false;

  // Layer panel overlay positions (left side, bottom aligned)
  readonly layerPanelPositions: ConnectedPosition[] = [
    {
      originX: "start",
      originY: "bottom",
      overlayX: "end",
      overlayY: "bottom",
    },
  ];

  // Chat panel state
  chatPanelOpen = false;

  readonly chatPanelPositions: ConnectedPosition[] = [
    {
      originX: "start",
      originY: "bottom",
      overlayX: "end",
      overlayY: "bottom",
    },
  ];

  // Modern compass hover state
  hoveredSection: string | null = null;

  private subscriptions: Subscription[] = [];

  @Input()
  isBaseMapActivated: boolean = false;

  @Input() agents: AgentDefinition[] = [];

  private readonly chatSession = inject(AiChatSessionService);

  protected readonly currentThreadId = this.chatSession.threadId;

  protected readonly selectedAgentId = this.chatSession.agentId;

  protected readonly activeAgent = computed(() => this.agents.find((a) => a.id === this.chatSession.agentId()) ?? null);

  constructor(
    protected googleAnalyticsService: GoogleAnalyticsService,
    protected mapService: MapService,
    protected flagService: FeatureFlagsService,
    private loadingService: LoadingService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.agents.length >= 1 && this.chatSession.agentId() === null) {
      this.chatSession.agentId.set(this.agents[0].id);
    }
  }

  ngAfterViewInit() {
    this.viewerService.image$.subscribe((event: any) => {
      if (event !== null) {
        const zoomInfo = this.mapService.getZoomInfo();
        this.lockZoomIn = zoomInfo.lockZoomIn;
        this.lockZoomOut = zoomInfo.lockZoomOut;
      }
    });

    this.viewerService.zoomEnd$.subscribe((event: any) => {
      if (event !== null) {
        this.lockZoomIn = event.lockZoomIn;
        this.lockZoomOut = event.lockZoomOut;
      }
    });

    // Subscribe to loading service for spinner (using 'tiles' loadingId)
    this.subscriptions.push(
      this.loadingService.showLoading$.subscribe((loadId: string) => {
        if (loadId === "tiles") {
          this.showLoading = true;
          this.cdr.markForCheck();
        }
      }),
    );

    this.subscriptions.push(
      this.loadingService.hideLoading$.subscribe((loadId: string) => {
        if (loadId === "tiles") {
          this.showLoading = false;
          this.cdr.markForCheck();
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  @Input()
  set aois(aois: IProjectAOI[]) {
    this._aois = aois;
  }

  get aois(): IProjectAOI[] {
    return this._aois;
  }

  @Input()
  set imageAOIs(aois: IProjectAOI[]) {
    this._imageAOIs = aois;
  }

  get imageAOIs(): IProjectAOI[] {
    return this._imageAOIs;
  }

  @Input()
  set centerAOI(aoi: IProjectAOI) {
    this._centerAOI = aoi;
    this.updateObliqueEnabled();
  }

  get centerAOI(): IProjectAOI {
    return this._centerAOI;
  }

  @Input()
  set mode(mode: any) {
    this._mode = mode;
    this.is3D = mode?.name === ViewerConstants.COLLECTION_MODES.OBLIQUE.name;
  }

  get mode(): any {
    return this._mode;
  }

  @Input()
  set zoom(event: any) {
    if (event !== null) {
      this.enableObliqueByZoom = event.zoom >= 19;
    }
  }

  @Input()
  set initialized(initialized: boolean) {
    this.updateInfraredStatus();
  }

  @Input()
  set orientation(orientation: string) {
    // Si es la primera vez (oldOrientation es undefined), inicializar rotación
    if (!this._orientation) {
      this.initializeControllerRotation(orientation);
    } else {
      this.updateControlOrientation(this._orientation, orientation);
    }
    this._orientation = orientation;
    this.updateMeasureOrientation(orientation);
  }

  get orientation(): string {
    return this._orientation;
  }

  /**
   * Changes the map viewing mode between 2D (VERTICAL) and 3D (OBLIQUE)
   */
  changeMode() {
    if (!this.isEnabled()) {
      return;
    }
    this.viewerService.changeMode();
    this.updateInfraredStatus();
  }

  /**
   * Changes the map orientation to a specified cardinal direction
   * @param orientation - Target orientation (N, S, E, W, N*, I)
   */
  changeOrientation(orientation: string) {
    if (this.lockRotation || !this.orientation || !this.isEnabled() || !this.availableOrientation(orientation)) {
      return;
    }
    this.viewerService.changeOrientation(orientation);

    // eslint-disable-next-line no-param-reassign
    orientation =
      orientation == ViewerConstants.DISCRETE_ORIENTATIONS.NADIR[0]
        ? ViewerConstants.DISCRETE_ORIENTATIONS.V
        : orientation;

    this.updateInfraredStatus();
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.CHANGE_ORIENTATION + orientation,
      GoogleAnalyticsConstantsCategory.COLLECTION_CONTROLLER,
      true,
    );
  }

  /**
   * Checks if the controller is enabled and ready for interaction
   * @returns True if controller is unlocked and has valid mode and orientation
   */
  isEnabled(): boolean {
    return !!(this.mode && this.orientation);
  }

  /**
   * Checks if the 3D button should be enabled
   * @returns True if 3D mode can be activated
   */
  enable3DButton(): boolean {
    return this.enableObliqueMode && this.enableObliqueByZoom && this.have3DOrientation() && !this.isBaseMapActivated;
  }

  /**
   * Checks if the current AOI has oblique imagery available
   * @returns True if oblique imagery exists
   */
  haveObliqueImagery(): boolean {
    return this.centerAOI?.hasObliques() || false;
  }

  /**
   * Checks if the current orientation has 3D data available
   * @returns True if 3D orientation data exists
   */
  have3DOrientation(): boolean {
    return this.centerAOI?.hasNonArchivedOrientation(this.orientation) || false;
  }

  /**
   * Gets tooltip message for 3D mode button based on current state
   * @returns Tooltip message or null if no message needed
   */
  get3DModeMessage(): string | null {
    if (!this.mode || !this.orientation) {
      return null;
    }

    if (!this.haveObliqueImagery()) {
      return "No 3D collection available for this AOI";
    }

    if (!this.enableObliqueByZoom) {
      return "Zoom in to enable 3D";
    }

    if (!this.have3DOrientation()) {
      return "No oblique images available for this location";
    }

    return null;
  }

  /**
   * Checks if a specific orientation is currently available
   * @param orientation - Orientation to check
   * @returns True if orientation is available
   */
  availableOrientation(orientation: string): boolean {
    if (this.mode.name === ViewerConstants.COLLECTION_MODES.VERTICAL.name) {
      return true;
    }
    return this.centerAOI?.hasNonArchivedOrientation(orientation) || false;
  }

  /**
   * Gets all available orientations as a record of booleans
   * @returns Record with orientation keys and availability values
   */
  getAvailableOrientations(): Record<string, boolean> {
    return {
      N: this.availableOrientation(ViewerConstants.DISCRETE_ORIENTATIONS.N),
      E: this.availableOrientation(ViewerConstants.DISCRETE_ORIENTATIONS.E),
      S: this.availableOrientation(ViewerConstants.DISCRETE_ORIENTATIONS.S),
      W: this.availableOrientation(ViewerConstants.DISCRETE_ORIENTATIONS.W),
    };
  }

  /**
   * Gets the current infrared status
   * @returns True if infrared mode is active
   */
  getInfraredStatus(): boolean {
    return this.viewerService ? this.viewerService.getInfraredStatus() : false;
  }

  /**
   * Increases map zoom level
   */
  zoomIn() {
    if (!this.lockZoomIn) {
      this.mapService.getCoreMap().zoomIn();
    }
  }

  /**
   * Decreases map zoom level
   */
  zoomOut() {
    if (!this.lockZoomOut) {
      this.mapService.getCoreMap().zoomOut();
    }
  }

  /**
   * Loads infrared image for current view
   */
  loadInfraredImage(): void {
    if (this.orientation !== ViewerConstants.DISCRETE_ORIENTATIONS.I) {
      this.viewerService.changeOrientation(ViewerConstants.DISCRETE_ORIENTATIONS.I);
    }
  }

  toggleInfrared() {
    const activeInfrared = this.viewerService.toggleInfrared();
    this.viewerService.updateLocationInfo();
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.TOGGLE_INFRARED_2D,
      GoogleAnalyticsConstantsCategory.COLLECTION_CONTROLLER,
      activeInfrared,
    );
  }

  /**
   * Loads nadir (vertical) view image
   */
  loadNadirImage(): void {
    if (this.orientation !== ViewerConstants.DISCRETE_ORIENTATIONS.NADIR[0]) {
      this.viewerService.changeOrientation(ViewerConstants.DISCRETE_ORIENTATIONS.NADIR[0]);
    }
  }

  /**
   * Checks if nadir button should be enabled
   * @returns True if nadir button should be shown
   */
  enableNadirButton(): boolean {
    return !this.enableObliqueMode && this.is3D && this.haveNadirOrientation();
  }

  /**
   * Checks if nadir orientation is available
   * @returns True if nadir orientation exists
   */
  haveNadirOrientation(): boolean {
    return this.centerAOI?.hasNonArchivedOrientation(ViewerConstants.DISCRETE_ORIENTATIONS.NADIR[0]) || false;
  }

  /**
   * Updates oblique mode availability based on current AOI
   */
  private updateObliqueEnabled() {
    let enable = false;
    if (this.centerAOI) {
      const imageAOI = this.imageAOIs.find((aoi) => aoi.getLayerId() === this.centerAOI.getLayerId());
      if (imageAOI) {
        enable = imageAOI.isEnabled();
      } else {
        enable = this.centerAOI.isEnabled3D() && this.centerAOI.isEnabled();
      }
    }
    this.enableObliqueMode = enable;
    this.updateInfraredStatus();
  }

  /**
   * Updates infrared button status based on current mode and orientation
   */
  private updateInfraredStatus() {
    const cirAvailable = this.centerAOI?.isCirAvailable(this.is3D);
    this.showInfraredButton3D = this.enableObliqueMode && this.is3D;
    this.enableInfraredButton =
      (this.viewerService.getZoom() >= ViewerConstants.COLLECTION_TIMELINE_ZOOM_LEVEL && cirAvailable) ||
      (this.viewerService.getZoom() < ViewerConstants.COLLECTION_TIMELINE_ZOOM_LEVEL &&
        !this.viewerService.getInfraredStatus());
  }

  /**
   * Initializes controller rotation based on initial orientation
   * @param orientation - Initial orientation (N, E, S, W)
   */
  private initializeControllerRotation(orientation: string): void {
    const compassPositions = ["N", "E", "S", "W"];
    let actualOrientation = orientation;

    // Handle NADIR and infrared orientations - treat as North
    if (
      ViewerConstants.DISCRETE_ORIENTATIONS.NADIR.includes(orientation) ||
      ViewerConstants.DISCRETE_ORIENTATIONS.I === orientation
    ) {
      actualOrientation = compassPositions[0]; // North
    }

    // Set initial rotation to bring selected orientation to top (0°)
    const orientationAngles: Record<string, number> = {
      N: 0, // North at top = 0° rotation
      E: 90, // East to top = 90° clockwise
      S: 180, // South to top = 180°
      W: -90, // West to top = 90° counter-clockwise
    };

    this.controllerRotation = orientationAngles[actualOrientation] || 0;
  }

  /**
   * Updates controller rotation based on orientation change
   * Uses same logic as map-controller to ensure identical behavior
   * Rotates incrementally in the shortest direction
   * @param oldOrientation - Previous orientation
   * @param newOrientation - New orientation
   */
  private updateControlOrientation(oldOrientation: string, newOrientation: string) {
    if (!this.orientation) {
      return;
    }
    let currentOrientation = oldOrientation;
    let nextOrientation = newOrientation;
    const compassPositions = ["N", "E", "S", "W"];
    const northOrientation = compassPositions[0];

    // Handle NADIR and infrared orientations - treat as North
    if (
      ViewerConstants.DISCRETE_ORIENTATIONS.NADIR.includes(currentOrientation) ||
      ViewerConstants.DISCRETE_ORIENTATIONS.I === currentOrientation
    ) {
      currentOrientation = northOrientation;
    }
    if (
      ViewerConstants.DISCRETE_ORIENTATIONS.NADIR.includes(newOrientation) ||
      ViewerConstants.DISCRETE_ORIENTATIONS.I === newOrientation
    ) {
      nextOrientation = northOrientation;
    }

    // Calculate rotation incrementally based on position difference
    // This ensures rotation in the shortest direction
    const currentPosition = compassPositions.indexOf(currentOrientation);
    const newPosition = compassPositions.indexOf(nextOrientation);
    const positionDiff = currentPosition - newPosition;

    switch (positionDiff) {
      case 0:
        // No rotation needed
        break;
      case 1:
      case -3:
        // Rotate clockwise 90°
        this.controllerRotation += 90;
        break;
      case 2:
      case -2:
        // Rotate 180° (doesn't matter which direction)
        this.controllerRotation -= 180;
        break;
      case 3:
      case -1:
        // Rotate counter-clockwise 90°
        this.controllerRotation -= 90;
        break;
      default:
        break;
    }
  }

  /**
   * Updates measure orientation when orientation changes
   * @param orientation - New orientation
   */
  private updateMeasureOrientation(orientation: string) {
    if (this.viewerService && typeof (this.viewerService as any).setMeasureOrientation === "function") {
      (this.viewerService as any).setMeasureOrientation(orientation);
    }
  }

  /**
   * Handles compass hover state
   * @param hovered - True if compass is being hovered
   */
  onCompassHover(hovered: boolean): void {
    this.compassHovered = hovered;
    this.cdr.markForCheck();
  }

  /**
   * Selects a specific orientation from the compass ring
   * @param orientation - Selected orientation (N, E, S, W)
   */
  selectOrientation(orientation: string): void {
    if (this.getAvailableOrientations()[orientation]) {
      this.changeOrientation(orientation);
    }
  }

  /**
   * Cycles through orientations (N → E → S → W → N)
   * Called when clicking the center button
   */
  cycleOrientation(): void {
    const orientations = ["N", "E", "S", "W"];
    const currentIndex = orientations.indexOf(this.orientation);

    // Find next available orientation
    for (let i = 0; i < orientations.length; i++) {
      const testIndex = (currentIndex + 1 + i) % orientations.length;
      const testOrientation = orientations[testIndex];
      if (this.getAvailableOrientations()[testOrientation]) {
        this.changeOrientation(testOrientation);
        return;
      }
    }
  }

  /**
   * Gets tooltip for compass center button
   * @returns Tooltip text
   */
  getCompassTooltip(): string {
    return "Click to cycle orientation";
  }

  /**
   * Toggles AI chat panel visibility
   */
  toggleChatPanel(): void {
    this.chatPanelOpen = !this.chatPanelOpen;
  }

  protected onAgentChange(agentId: string): void {
    this.chatSession.switchAgent(agentId);
  }

  protected startNewChat(): void {
    this.chatSession.startNewChat();
  }

  /**
   * Toggles layer panel visibility
   */
  toggleLayerPanel(): void {
    this.layerPanelOpen = !this.layerPanelOpen;
  }

  /**
   * Handles imagery toggle from layer panel
   */
  onImageryToggle(): void {
    this.viewerService.getCollections().forEach((c) => this.viewerService.changeAOIVisibility(c.id, true, true));
    const vexcelBaseLayer = this.mapService.getOverlayManager().getVexcelBaseLayer();
    if (vexcelBaseLayer) {
      vexcelBaseLayer.setVisible(true);
    }
    this.isImageryShown = true;
    this.viewerService.setActivatedBaseMap(false);
  }

  /**
   * Handles basemap toggle from layer panel
   */
  onBasemapToggle(): void {
    this.viewerService.getCollections().forEach((c) => this.viewerService.changeAOIVisibility(c.id, false, true));
    const vexcelBaseLayer = this.mapService.getOverlayManager().getVexcelBaseLayer();
    if (vexcelBaseLayer) {
      vexcelBaseLayer.setVisible(false);
    }
    this.isImageryShown = false;
    this.viewerService.setActivatedBaseMap(true);
  }

  /**
   * Handles contrast toggle from layer panel
   */
  onContrastToggle(): void {
    if (this.centerAOI) {
      this.viewerService.toggleContrastEnhance(this.centerAOI.getLayerId());
    }
  }

  /**
   * Handles overlay layer reordering
   */
  onOverlayOrderChange(event: CdkDragDrop<Viewerlayer[]>): void {
    // Get the array from the drop event data (passed from template)
    const layers = event.container.data || [];
    moveItemInArray(layers, event.previousIndex, event.currentIndex);
    this.viewerService.updateOverlayOrder(layers);
  }

  /**
   * Handles layer visibility change
   */
  onLayerVisibilityChange(event: { layer: Viewerlayer; visibility: boolean }): void {
    const { layer, visibility } = event;

    if (layer.owner) {
      this.viewerService.changeUserLayerVisibility(layer.id, visibility);
      this.viewerService.updateUserLayer(layer);
    } else {
      this.viewerService.changeLayerVisibility(layer.id, visibility, false);

      if (layer?.layerType === ViewerConstants.LAYER_TYPE.WMSLAYER && layer.layersAvailable) {
        layer.layersAvailable.map((layerAvailable) => (layerAvailable.visible = visibility));
        this.mapService.getCoreMap().changeVisibilityWMSChild(layer.id);
      } else {
        layer.childs?.map((child) => this.viewerService.changeLayerVisibility(child.id, visibility, false));
      }
    }
  }

  /**
   * Handles child layer visibility change
   */
  onChildVisibilityChange(event: { layer: any; child: any; visibility: boolean }): void {
    const { layer, child, visibility } = event;

    if (child) {
      child.visible = visibility;
    }

    layer.visible = !!layer.layersAvailable?.find((l) => l.visible) || !!layer.childs?.find((l) => l.visible);

    if (layer?.layerType === ViewerConstants.LAYER_TYPE.WMSLAYER) {
      this.mapService.getCoreMap().changeVisibilityWMSChild(layer.id);
    } else {
      this.viewerService.changeLayerVisibility(child.id, visibility);
    }
  }

  /**
   * Handles zoom to feature
   */
  onFeatureFit(event: { layer: any; feature: any }): void {
    if (event.feature && event.feature.bbox) {
      this.mapService.getCoreMap().fitToExtent(event.feature.bbox, {});
    }
  }

  /**
   * Handles fit to bounding box
   */
  onBboxFit(event: { bbox: any; fitOptions: any }): void {
    if (event.bbox) {
      this.mapService.getCoreMap().fitToExtent(event.bbox, event.fitOptions || {});
    }
  }

  /**
   * Handles layer removal
   */
  onLayerRemove(layer: Viewerlayer): void {
    this.viewerService.removeUserLayer(layer.id);
  }

  /**
   * Handles modern compass section hover
   */
  onModernSectionHover(section: string): void {
    this.hoveredSection = section;
    this.cdr.markForCheck();
  }

  /**
   * Handles modern compass section leave
   */
  onModernSectionLeave(): void {
    this.hoveredSection = null;
    this.cdr.markForCheck();
  }

  /**
   * Handles modern compass orientation click
   */
  onModernOrientationClick(orientation: string): void {
    // Check if orientation is available (already handles 3D mode internally)
    const availableOrientations = this.getAvailableOrientations();
    if (!availableOrientations[orientation]) {
      return; // Disabled
    }
    // Reutilizar método existente
    this.selectOrientation(orientation);
  }
}
